import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { getIsUserSignedIn } from "@gamedev-web/gamedev-sdk-core";

import { getPartnerAccessDeniedPath } from "../constants/urls";
import { redirectToSignIn } from "../constants/urlsClientSide";
import { RouteNames } from "../partnerConfig/routeNames";
import { OnboardingUrls } from "../partnerConfig/routes";
import { xboxOnboardActions } from "../redux/actions/xboxOnboardActions";
import {
  getXboxOnboardData,
  getXboxOnboardIsLoading,
} from "../redux/selectors/xboxOnboardSelectors";
import {
  XboxOnboardApplicationStatus,
  XboxOnboardResponse,
} from "../server/crm/api/models/xboxOnboardModels";
import { isPartnerCenterEnrollmentFinished } from "./partnerEnrollmentHelpers";

/**
 * Access levels for partner routes:
 * - Public: No auth required (landing, signup)
 * - Auth: Requires sign-in only (xbox onboard form)
 * - PartnerSettings: Requires an onboarding profile or account; NDA not required
 * - NdaPending: Requires NDA sent or signed (dashboard, contracts)
 * - NdaSigned: Requires NDA fully signed (settings, pitch)
 * - NdaSignedOrAaa: Requires NDA signed unless the account is assigned to the AAA Hub
 * - EnrollmentFinished: Requires NDA signed and Partner Center enrollment complete or rejected
 */
export enum PartnerAccessLevel {
  Public = "public",
  Auth = "auth",
  PartnerSettings = "partnerSettings",
  NdaPending = "ndaPending",
  NdaSigned = "ndaSigned",
  NdaSignedOrAaa = "ndaSignedOrAaa",
  EnrollmentFinished = "enrollmentFinished",
}

export enum PartnerAccountRequirement {
  Any = "any",
  NonAaa = "nonAaa",
}

type PartnerRouteState = Pick<
  XboxOnboardResponse,
  "status" | "hubType" | "partnerCenterEnrollmentStatus" | "accessDeniedCode"
>;

export const getPartnerRouteRedirect = (
  accessLevel: PartnerAccessLevel,
  data: PartnerRouteState,
  accountRequirement = PartnerAccountRequirement.Any
): string | null => {
  if (accessLevel === PartnerAccessLevel.Public) {
    return null;
  }

  if (data.accessDeniedCode !== undefined) {
    return getPartnerAccessDeniedPath(data.accessDeniedCode);
  }

  if (accessLevel === PartnerAccessLevel.Auth) {
    return data.status === XboxOnboardApplicationStatus.NdaSent ||
      data.status === XboxOnboardApplicationStatus.Submitted ||
      data.status === XboxOnboardApplicationStatus.NdaSigned
      ? OnboardingUrls[RouteNames.PartnerHome]
      : null;
  }

  if (accountRequirement === PartnerAccountRequirement.NonAaa && data.hubType === "aaa") {
    return OnboardingUrls[RouteNames.PartnerHome];
  }

  if (accessLevel === PartnerAccessLevel.PartnerSettings) {
    return data.hubType === "none" ? OnboardingUrls[RouteNames.PartnerHome] : null;
  }

  if (accessLevel === PartnerAccessLevel.NdaPending) {
    return data.status === XboxOnboardApplicationStatus.Initial ||
      data.status === XboxOnboardApplicationStatus.FormReady
      ? OnboardingUrls[RouteNames.XboxOnboard]
      : null;
  }

  if (accessLevel === PartnerAccessLevel.NdaSignedOrAaa && data.hubType === "aaa") {
    return null;
  }

  if (data.status !== XboxOnboardApplicationStatus.NdaSigned) {
    return OnboardingUrls[RouteNames.PartnerHome];
  }

  if (accessLevel === PartnerAccessLevel.EnrollmentFinished) {
    const aaaEnrollmentIsSkipped = data.hubType === "aaa";
    if (
      !aaaEnrollmentIsSkipped &&
      !isPartnerCenterEnrollmentFinished(data.partnerCenterEnrollmentStatus)
    ) {
      return OnboardingUrls[RouteNames.PartnerHome];
    }
  }

  return null;
};

/**
 * Route guard hook for partner pages. Checks auth and NDA status,
 * redirecting users who don't meet the access requirements.
 *
 * Returns `true` when the page is safe to render, `false` while redirecting or loading.
 */
export const usePartnerRouteGuard = (
  accessLevel: PartnerAccessLevel,
  accountRequirement = PartnerAccountRequirement.Any
): boolean => {
  const isSignedIn = useSelector(getIsUserSignedIn);
  const data = useSelector(getXboxOnboardData);
  const isLoading = useSelector(getXboxOnboardIsLoading);
  const history = useHistory();
  const dispatch = useDispatch();

  // Fetch xboxOnboard data if not loaded (for direct URL navigation)
  useEffect(() => {
    if (accessLevel !== PartnerAccessLevel.Public && isSignedIn && !data && !isLoading) {
      dispatch(xboxOnboardActions.fetchData.start());
    }
  }, [accessLevel, isSignedIn, data, isLoading, dispatch]);

  // Redirect to sign-in for all non-public pages
  useEffect(() => {
    if (accessLevel !== PartnerAccessLevel.Public && typeof window !== "undefined" && !isSignedIn) {
      redirectToSignIn();
    }
  }, [accessLevel, isSignedIn]);

  // Redirect once lifecycle data proves the requested page is not currently accessible.
  useEffect(() => {
    if (accessLevel === PartnerAccessLevel.Public || !isSignedIn || isLoading || !data) {
      return;
    }

    const redirectPath = getPartnerRouteRedirect(accessLevel, data, accountRequirement);
    if (redirectPath) {
      history.replace(redirectPath);
    }
  }, [accessLevel, accountRequirement, isSignedIn, isLoading, data, history]);

  // Public pages — always allowed
  if (accessLevel === PartnerAccessLevel.Public) {
    return true;
  }

  if (!isSignedIn) {
    return false;
  }

  if (accessLevel === PartnerAccessLevel.Auth) {
    return true;
  }

  // For NDA-gated pages, wait for data to load before rendering
  if (!data || isLoading) {
    return false;
  }

  return getPartnerRouteRedirect(accessLevel, data, accountRequirement) === null;
};
